import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useCategories } from "@/contexts/CategoriesContext";
import { useHousehold } from "@/contexts/HouseholdContext";
import { fmtCurrency, fmtDate, daysUntil, monthlyCostOf, BILLING_CYCLES, PAYMENT_METHODS } from "@/lib/format";
import { toast } from "sonner";
import EmptyState from "@/components/EmptyState";
import StatCard from "@/components/StatCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    RefreshCw, Plus, Pencil, Trash2, Calendar as CalIcon,
    DollarSign, Activity, LayoutGrid, List as ListIcon, CheckCircle2, Users, User as UserIcon,
} from "lucide-react";

const PALETTE = ["#4F46E5","#10B981","#0EA5E9","#F59E0B","#EF4444","#8B5CF6","#EC4899","#14B8A6"];

// Advance a local Date by one billing cycle
function advanceDate(d, billingCycle) {
    const n = new Date(d);
    if (billingCycle === "weekly") { n.setDate(n.getDate() + 7); }
    else if (billingCycle === "quarterly") { n.setMonth(n.getMonth() + 3); }
    else if (billingCycle === "yearly") { n.setFullYear(n.getFullYear() + 1); }
    else { n.setMonth(n.getMonth() + 1); } // monthly
    return n;
}

// Returns the next FUTURE renewal date for an active Auto sub, as YYYY-MM-DD
function computeNextDate(dateStr, billingCycle) {
    const [y, mo, dy] = dateStr.split("-").map(Number);
    let d = new Date(y, mo - 1, dy);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    while (d < today) d = advanceDate(d, billingCycle);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function RenewalStatus({ paymentOption, paid, dleft }) {
    if (paid) return <span className="text-xs font-semibold text-emerald-600">✓ Paid</span>;
    if (paymentOption === "auto") return <span className="text-xs font-semibold text-indigo-600">⚡ Auto</span>;
    if (dleft < 0) return <span className="text-xs font-semibold text-rose-600">Overdue</span>;
    if (dleft === 0) return <span className="text-xs font-semibold text-amber-600">Today</span>;
    if (dleft <= 3) return <span className="text-xs font-semibold text-rose-600">In {dleft}d</span>;
    if (dleft <= 7) return <span className="text-xs font-semibold text-amber-600">In {dleft}d</span>;
    return <span className="text-xs font-semibold text-slate-500">In {dleft}d</span>;
}

export default function Subscriptions() {
    const { user } = useAuth();
    const cur = user?.currency || "USD";
    const { categoryNames } = useCategories();
    const { isMember, memberName } = useHousehold();

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("upcoming");
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [view, setView] = useState("list");
    // Opens with the user's preferred Combined/Just me default (Settings → Preferences).
    const [scope, setScope] = useState(() => user?.default_scope || "mine");
    const [editing, setEditing] = useState(null);
    const [deleteId, setDeleteId] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (categoryFilter !== "all") params.category = categoryFilter;
            if (statusFilter !== "all") params.status = statusFilter;
            if (isMember) params.scope = scope;
            const { data } = await api.get("/subscriptions", { params });
            setItems(data);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [categoryFilter, statusFilter, scope, isMember]);

    // Tab filtering is client-side:
    // Upcoming = active Auto subs whose NEXT renewal is within 15 days
    //          + active Manual subs that are unpaid and overdue or within 15 days
    // History  = inactive subs
    //          + active Auto subs with a past stored renewal date (already auto-charged)
    //          + active Manual subs marked paid
    // All      = everything
    const displayed = useMemo(() => {
        if (tab === "all") return items;
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const in15 = new Date(today); in15.setDate(today.getDate() + 15);
        return items.filter(s => {
            const [y, mo, dy] = s.renewal_date.split("-").map(Number);
            const rd = new Date(y, mo - 1, dy);
            if (tab === "upcoming") {
                if (s.status !== "active") return false;
                if (s.payment_option === "auto") {
                    const [ny, nm, nd] = computeNextDate(s.renewal_date, s.billing_cycle).split("-").map(Number);
                    const next = new Date(ny, nm - 1, nd);
                    return next >= today && next <= in15;
                }
                return !s.paid && rd <= in15;
            }
            // history
            if (s.status === "inactive") return true;
            if (s.payment_option === "auto" && rd < today) return true;
            if (s.payment_option === "manual" && s.paid) return true;
            return false;
        });
    }, [items, tab]);

    const totals = useMemo(() => {
        const active = items.filter(i => i.status === "active");
        const monthly = active.reduce((s, i) => s + monthlyCostOf(i.cost, i.billing_cycle), 0);
        return { monthly, yearly: monthly * 12, count: active.length };
    }, [items]);

    const markPaid = async (sid) => {
        try {
            await api.patch(`/subscriptions/${sid}`, { paid: true });
            toast.success("Marked as paid — renewed for next cycle");
            load();
        } catch { toast.error("Failed to mark as paid"); }
    };

    const onDelete = async () => {
        if (!deleteId) return;
        try {
            await api.delete(`/subscriptions/${deleteId}`);
            toast.success("Removed");
            setItems(prev => prev.filter(i => i.id !== deleteId));
        } catch { toast.error("Failed to delete"); }
        finally { setDeleteId(null); }
    };

    const newSubDefaults = {
        _new: true, billing_cycle: "monthly", category: "Entertainment",
        payment_method: "Credit Card", renewal_date: new Date().toISOString().slice(0, 10),
        status: "active", payment_option: "auto", is_trial: false,
    };

    return (
        <div className="space-y-4" data-testid="subs-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div>
                    <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">Subscriptions</h1>
                    <p className="text-sm text-slate-500 mt-1">Recurring services you pay for</p>
                </div>
                <div className="flex items-center gap-2">
                    {isMember ? (
                        <Tabs value={scope} onValueChange={setScope}>
                            <TabsList className="bg-slate-100 rounded-xl p-1">
                                <TabsTrigger data-testid="subs-scope-household" value="household" className="rounded-lg data-[state=active]:bg-white gap-1.5">
                                    <Users className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Combined</span>
                                </TabsTrigger>
                                <TabsTrigger data-testid="subs-scope-mine" value="mine" className="rounded-lg data-[state=active]:bg-white gap-1.5">
                                    <UserIcon className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Just me</span>
                                </TabsTrigger>
                            </TabsList>
                        </Tabs>
                    ) : null}
                    <Tabs value={view} onValueChange={setView}>
                        <TabsList className="bg-slate-100 rounded-xl p-1">
                            <TabsTrigger data-testid="subs-view-cards" value="cards" className="rounded-lg data-[state=active]:bg-white px-2.5">
                                <LayoutGrid className="w-4 h-4" />
                            </TabsTrigger>
                            <TabsTrigger data-testid="subs-view-list" value="list" className="rounded-lg data-[state=active]:bg-white px-2.5">
                                <ListIcon className="w-4 h-4" />
                            </TabsTrigger>
                        </TabsList>
                    </Tabs>
                    <Button onClick={() => setEditing(newSubDefaults)} data-testid="new-sub-btn" className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl gap-1.5">
                        <Plus className="w-4 h-4" /> Add
                    </Button>
                </div>
            </div>

            {/* Filter row */}
            <div className="card-elev rounded-2xl p-2 sm:p-3 flex items-center gap-2 sm:gap-3 overflow-x-auto no-scrollbar sm:overflow-visible">
                {/* Mobile: Upcoming/History/All as a compact dropdown instead of tabs */}
                <Select value={tab} onValueChange={setTab}>
                    <SelectTrigger data-testid="tab-select-mobile" className="sm:hidden h-9 rounded-xl w-28 px-2 text-xs flex-shrink-0"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="upcoming">Upcoming</SelectItem>
                        <SelectItem value="history">History</SelectItem>
                        <SelectItem value="all">All</SelectItem>
                    </SelectContent>
                </Select>
                <Tabs value={tab} onValueChange={setTab} className="hidden sm:block flex-shrink-0">
                    <TabsList className="bg-white border border-slate-200/80 rounded-xl p-1">
                        <TabsTrigger value="upcoming" data-testid="tab-upcoming" className="rounded-lg data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700 px-4">Upcoming</TabsTrigger>
                        <TabsTrigger value="history" data-testid="tab-history" className="rounded-lg data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700 px-4">History</TabsTrigger>
                        <TabsTrigger value="all" data-testid="tab-all" className="rounded-lg data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700 px-4">All</TabsTrigger>
                    </TabsList>
                </Tabs>
                <div className="flex items-center flex-nowrap sm:flex-wrap gap-2 sm:ml-auto">
                    <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                        <SelectTrigger className="h-9 sm:h-10 rounded-xl w-28 sm:w-44 px-2 sm:px-3 text-xs sm:text-sm flex-shrink-0"><SelectValue placeholder="All categories" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All categories</SelectItem>
                            {categoryNames.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="h-9 sm:h-10 rounded-xl w-24 sm:w-36 px-2 sm:px-3 text-xs sm:text-sm flex-shrink-0"><SelectValue placeholder="All statuses" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All statuses</SelectItem>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="inactive">Inactive</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Stat cards — mobile: compact, always one row of 3. sm/lg: unchanged full-size 3-across. */}
            <div className="grid grid-cols-3 gap-2 sm:gap-4">
                <StatCard compact testid="stat-mo-cost" label="Monthly cost" value={fmtCurrency(totals.monthly, cur)} sublabel="All active subs" accent="indigo" icon={DollarSign} />
                <StatCard compact testid="stat-yr-cost" label="Yearly cost" value={fmtCurrency(totals.yearly, cur)} sublabel="Projected" accent="amber" icon={CalIcon} />
                <StatCard compact testid="stat-active" label="Active services" value={totals.count} sublabel="Currently billed" accent="emerald" icon={Activity} />
            </div>

            {/* Content */}
            {loading ? (
                <div className="card-elev rounded-2xl p-10 text-center text-slate-400 text-sm">Loading…</div>
            ) : displayed.length === 0 ? (
                <div className="card-elev rounded-2xl">
                    <EmptyState
                        icon={RefreshCw}
                        title="No subscriptions"
                        description={
                            tab === "upcoming" ? "No renewals due in the next 15 days." :
                            tab === "history" ? "No inactive subscriptions." :
                            "No subscriptions yet."
                        }
                        action={tab !== "history" && (
                            <Button onClick={() => setEditing(newSubDefaults)} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl">
                                <Plus className="w-4 h-4 mr-1.5" /> Add subscription
                            </Button>
                        )}
                    />
                </div>
            ) : view === "cards" ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="subs-grid">
                    {displayed.map((s, i) => {
                        // For active Auto subs show the next future renewal; History shows stored date
                        const displayDate = (s.payment_option === "auto" && s.status === "active" && tab !== "history")
                            ? computeNextDate(s.renewal_date, s.billing_cycle)
                            : s.renewal_date;
                        const dleft = daysUntil(displayDate);
                        const color = s.color || PALETTE[i % PALETTE.length];
                        const yearly = monthlyCostOf(s.cost, s.billing_cycle) * 12;
                        return (
                            <div key={s.id} data-testid={`sub-${s.id}`} className="card-elev rounded-2xl p-5 relative">
                                <div className="absolute top-3 right-3 flex gap-1">
                                    {s.is_trial && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-md bg-rose-500 text-white">Trial</span>}
                                    {s.status !== "active" && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-slate-100 text-slate-500">Inactive</span>}
                                </div>
                                <div className="flex items-start gap-3 mb-4">
                                    <span className="w-12 h-12 rounded-2xl grid place-items-center text-white font-display font-extrabold text-lg" style={{ backgroundColor: color }}>
                                        {s.name.slice(0, 1)}
                                    </span>
                                    <div className="min-w-0">
                                        <div className="font-display text-lg font-bold text-slate-900 truncate">{s.name}</div>
                                        <div className="text-xs text-slate-500 flex items-center gap-1.5">
                                            {s.category} · {s.billing_cycle}
                                            {isMember && scope === "household" && s.user_id !== user.id ? (
                                                <span className="px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-indigo-50 text-indigo-600">{memberName(s.user_id)}</span>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-end justify-between mb-3">
                                    <div>
                                        <div className="font-display text-2xl font-extrabold text-slate-900 tabular-nums">{fmtCurrency(s.cost, cur)}</div>
                                        <div className="text-xs text-slate-500">≈ {fmtCurrency(yearly, cur)}/yr</div>
                                    </div>
                                    <RenewalStatus paymentOption={s.payment_option} paid={s.paid} dleft={dleft} />
                                </div>
                                <div className="text-xs text-slate-500 mb-4">
                                    {tab === "history" && s.payment_option === "auto" ? "Charged" : "Renews"} {fmtDate(displayDate)} · {s.payment_method || "—"}
                                </div>
                                {s.user_id === user.id ? (
                                    <div className="flex justify-end gap-1 pt-3 border-t border-slate-100">
                                        {s.payment_option === "manual" && !s.paid && s.status === "active" && (
                                            <button onClick={() => markPaid(s.id)} title="Mark as paid" className="p-1.5 rounded-lg text-slate-500 hover:bg-emerald-50 hover:text-emerald-600">
                                                <CheckCircle2 className="w-4 h-4" />
                                            </button>
                                        )}
                                        <button onClick={() => setEditing({ ...s, renewal_date: s.renewal_date?.slice(0, 10) })} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700"><Pencil className="w-4 h-4" /></button>
                                        <button onClick={() => setDeleteId(s.id)} className="p-1.5 rounded-lg text-slate-500 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                                    </div>
                                ) : (
                                    <div className="flex justify-end pt-3 border-t border-slate-100 text-xs text-slate-400">{memberName(s.user_id)}'s subscription</div>
                                )}
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="card-elev rounded-2xl overflow-hidden" data-testid="subs-list">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50/70 text-slate-500 text-xs uppercase tracking-wider">
                                <tr>
                                    <th className="text-left font-semibold px-5 py-3">Service</th>
                                    <th className="text-left font-semibold px-5 py-3 hidden md:table-cell">Category</th>
                                    <th className="text-left font-semibold px-5 py-3 hidden lg:table-cell">Cycle</th>
                                    <th className="text-left font-semibold px-5 py-3 hidden md:table-cell">Renews</th>
                                    <th className="text-right font-semibold px-5 py-3">Cost</th>
                                    <th className="text-right font-semibold px-5 py-3 hidden lg:table-cell">Yearly</th>
                                    <th className="text-right font-semibold px-5 py-3 w-32"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {displayed.map((s, i) => {
                                    const displayDate = (s.payment_option === "auto" && s.status === "active" && tab !== "history")
                                        ? computeNextDate(s.renewal_date, s.billing_cycle)
                                        : s.renewal_date;
                                    const dleft = daysUntil(displayDate);
                                    const color = s.color || PALETTE[i % PALETTE.length];
                                    const yearly = monthlyCostOf(s.cost, s.billing_cycle) * 12;
                                    return (
                                        <tr key={s.id} data-testid={`sub-row-${s.id}`} className="hover:bg-slate-50/50 transition-colors">
                                            <td className="px-5 py-3.5">
                                                <div className="flex items-center gap-3">
                                                    <span className="w-9 h-9 rounded-xl grid place-items-center text-white font-display font-extrabold" style={{ backgroundColor: color }}>{s.name.slice(0, 1)}</span>
                                                    <div>
                                                        <div className="font-semibold text-slate-900">{s.name}</div>
                                                        <div className="text-xs text-slate-500 md:hidden">{s.category} · {s.billing_cycle}</div>
                                                        <div className="flex gap-1 flex-wrap mt-0.5">
                                                            {s.is_trial && <span className="inline-block text-[11px] font-semibold px-1.5 py-0.5 rounded-md bg-rose-500 text-white">Trial</span>}
                                                            {s.status !== "active" && <span className="inline-block text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-500">Inactive</span>}
                                                            {isMember && scope === "household" && s.user_id !== user.id ? (
                                                                <span className="inline-block px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-indigo-50 text-indigo-600">{memberName(s.user_id)}</span>
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-5 py-3.5 hidden md:table-cell">
                                                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-slate-100 text-slate-700">{s.category}</span>
                                            </td>
                                            <td className="px-5 py-3.5 text-slate-600 hidden lg:table-cell capitalize">{s.billing_cycle}</td>
                                            <td className="px-5 py-3.5 hidden md:table-cell">
                                                <div className="text-slate-600">{fmtDate(displayDate)}</div>
                                                <RenewalStatus paymentOption={s.payment_option} paid={s.paid} dleft={dleft} />
                                            </td>
                                            <td className="px-5 py-3.5 text-right font-display font-bold text-slate-900 tabular-nums">{fmtCurrency(s.cost, cur)}</td>
                                            <td className="px-5 py-3.5 text-right text-slate-500 hidden lg:table-cell tabular-nums">{fmtCurrency(yearly, cur)}</td>
                                            <td className="px-5 py-3.5 text-right">
                                                <div className="inline-flex gap-1">
                                                    {s.user_id === user.id ? (
                                                        <>
                                                            {s.payment_option === "manual" && !s.paid && s.status === "active" && (
                                                                <button onClick={() => markPaid(s.id)} title="Mark as paid" className="p-1.5 rounded-lg text-slate-500 hover:bg-emerald-50 hover:text-emerald-600">
                                                                    <CheckCircle2 className="w-4 h-4" />
                                                                </button>
                                                            )}
                                                            <button onClick={() => setEditing({ ...s, renewal_date: s.renewal_date?.slice(0, 10) })} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700"><Pencil className="w-4 h-4" /></button>
                                                            <button onClick={() => setDeleteId(s.id)} className="p-1.5 rounded-lg text-slate-500 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                                                        </>
                                                    ) : (
                                                        <span className="text-xs text-slate-400">{memberName(s.user_id)}'s</span>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <SubEditor item={editing} onClose={() => setEditing(null)} onSaved={load} />

            <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Remove subscription?</AlertDialogTitle>
                        <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={onDelete} className="bg-rose-600 hover:bg-rose-700 text-white">Remove</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function SubEditor({ item, onClose, onSaved }) {
    const { categoryNames } = useCategories();
    
    const [form, setForm] = useState(item || {});
    const [saving, setSaving] = useState(false);
    useEffect(() => { setForm(item || {}); }, [item]);
    if (!item) return null;

    const submit = async () => {
        if (!form.name || !form.cost) { toast.error("Name and cost required"); return; }
        try {
            setSaving(true);
            const payload = {
                name: form.name, cost: parseFloat(form.cost),
                billing_cycle: form.billing_cycle || "monthly",
                renewal_date: form.renewal_date,
                payment_method: form.payment_method || null,
                category: form.category || "Other",
                notes: form.notes || null,
                color: form.color || null,
                status: form.status || "active",
                payment_option: form.payment_option || "auto",
                is_trial: !!form.is_trial,
            };
            if (form._new) await api.post("/subscriptions", payload);
            else await api.patch(`/subscriptions/${form.id}`, payload);
            toast.success(form._new ? "Subscription added" : "Updated");
            onSaved(); onClose();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="sm:max-w-lg rounded-2xl" data-testid="sub-editor">
                <DialogHeader><DialogTitle className="font-display text-xl">{form._new ? "Add subscription" : "Edit subscription"}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <FieldL label="Name"><Input data-testid="se-name" value={form.name || ""} onChange={e => setForm({ ...form, name: e.target.value })} /></FieldL>
                    <div className="grid grid-cols-2 gap-3">
                        <FieldL label="Cost"><Input data-testid="se-cost" type="number" step="0.01" value={form.cost || ""} onChange={e => setForm({ ...form, cost: e.target.value })} /></FieldL>
                        <FieldL label="Billing cycle">
                            <Select value={form.billing_cycle || "monthly"} onValueChange={v => setForm({ ...form, billing_cycle: v })}>
                                <SelectTrigger data-testid="se-cycle"><SelectValue /></SelectTrigger>
                                <SelectContent>{BILLING_CYCLES.map(b => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}</SelectContent>
                            </Select>
                        </FieldL>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <FieldL label="Renewal date"><Input data-testid="se-renewal" type="date" value={form.renewal_date || ""} onChange={e => setForm({ ...form, renewal_date: e.target.value })} /></FieldL>
                        <FieldL label="Category">
                            <Select value={form.category || "Other"} onValueChange={v => setForm({ ...form, category: v })}>
                                <SelectTrigger data-testid="se-cat"><SelectValue /></SelectTrigger>
                                <SelectContent>{categoryNames.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                            </Select>
                        </FieldL>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <FieldL label="Payment method">
                            <Select value={form.payment_method || "Credit Card"} onValueChange={v => setForm({ ...form, payment_method: v })}>
                                <SelectTrigger data-testid="se-pay"><SelectValue /></SelectTrigger>
                                <SelectContent>{PAYMENT_METHODS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                            </Select>
                        </FieldL>
                        <FieldL label="Status">
                            <Select value={form.status || "active"} onValueChange={v => setForm({ ...form, status: v })}>
                                <SelectTrigger data-testid="se-status"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="active">Active</SelectItem>
                                    <SelectItem value="inactive">Inactive</SelectItem>
                                </SelectContent>
                            </Select>
                        </FieldL>
                    </div>
                    <FieldL label="Payment option">
                        <Select value={form.payment_option || "auto"} onValueChange={v => setForm({ ...form, payment_option: v })}>
                            <SelectTrigger data-testid="se-payment-option"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="auto">Auto — charged automatically</SelectItem>
                                <SelectItem value="manual">Manual — I pay each cycle</SelectItem>
                            </SelectContent>
                        </Select>
                    </FieldL>
                    <FieldL label="Notes"><Textarea data-testid="se-notes" rows={2} value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} /></FieldL>
                    <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none pt-1">
                        <input type="checkbox" checked={!!form.is_trial} onChange={e => setForm({ ...form, is_trial: e.target.checked })} data-testid="se-trial" className="rounded accent-rose-500" />
                        <span>Trial <span className="text-xs text-slate-400">(marks this as a free/paid trial)</span></span>
                    </label>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={onClose}>Cancel</Button>
                    <Button data-testid="se-save" disabled={saving} onClick={submit} className="bg-indigo-600 hover:bg-indigo-700 text-white">{form._new ? "Create" : "Save"}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function FieldL({ label, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-700">{label}</Label>
            {children}
        </div>
    );
}
